import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Supabase client (anon role) — Family
 *
 * Wraps `createClient` with environment-variable validation so a missing .env
 * surfaces a clear error instead of a cryptic runtime crash.
 *
 * The service-role client is NEVER instantiated in browser code (CLAUDE.md §10).
 * Service-role access happens only inside Edge Functions.
 */

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = Boolean(url && anonKey);

let cachedClient: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient {
  if (!isSupabaseConfigured) {
    throw new Error(
      'Supabase configuration missing. Copy .env.example to .env and fill in VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (see SETUP.md §1).',
    );
  }
  if (!cachedClient) {
    cachedClient = createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true, // enables magic-link callback handling
        // Oturum 8 21:30 — Begüm Google OAuth login loop bug fix.
        // Supabase JS v2.46 default 'pkce' flow code_verifier'ı localStorage'a yazar
        // ve callback'te aynı localStorage'dan okur. PWA standalone + iOS Safari
        // context split, ITP, incognito gibi durumlarda verifier kaybolur →
        // exchange fail → session set olmaz → infinite login loop.
        // 'implicit' flow access_token'ı URL hash fragment'ına gömer, verifier
        // gerektirmez → PWA + Safari + incognito uyumlu. Aile-içi düşük risk
        // uygulama için kabul edilebilir trade-off (CLAUDE.md §10).
        flowType: 'implicit',
      },
    });
  }
  return cachedClient;
}
